<template>
  <template v-if="mode === 'audio' && menu">
    <div :ref="setRootRef" class="audio-with-menu">
      <audio
        v-bind="$attrs"
        :src="authUrl"
        controls
        controlslist="nodownload noplaybackrate"
        @play="syncPlaybackRate"
        @canplay="syncPlaybackRate"
      />
      <a-popover
        :visible="menuVisible"
        trigger="click"
        placement="bottomRight"
        :get-popup-container="triggerNode => triggerNode.parentNode"
        :overlay-style="{ width: 'max-content' }"
        @visibleChange="handleMenuVisibleChange"
      >
        <template #content>
          <div class="menu-item" @click="handleDownload">下载</div>
          <div class="menu-item" @click="speedVisible = !speedVisible">
            播放速度
            <span class="speed-arrow">{{ speedVisible ? '▲' : '▶' }}</span>
          </div>
          <template v-if="speedVisible">
            <div
              v-for="rate in playbackRateOptions"
              :key="rate"
              class="menu-item speed-option"
              :class="{ active: playbackRate === rate }"
              @click="handleSetPlaybackRate(rate)"
            >
              {{ rate === 1 ? '正常' : `${rate}x` }}
            </div>
          </template>
          <div class="menu-item" @click="handleVoiceToText">
            {{ voiceText.visible ? '收起文字' : '语音转文字' }}
          </div>
        </template>
        <a-button type="text" size="small" class="audio-menu-trigger">
          <MoreOutlined />
        </a-button>
      </a-popover>
    </div>
    <transition name="voice-text-fade">
      <div v-if="voiceText.visible" class="voice-text-content">
        <span v-if="voiceText.loading" class="voice-text-loading">
          <a-spin size="small" />
        </span>
        <template v-else>{{ voiceText.text }}</template>
      </div>
    </transition>
  </template>
  <audio v-else-if="mode === 'audio'" v-bind="$attrs" :src="authUrl" />
  <video v-else-if="mode === 'video'" v-bind="$attrs" :src="authUrl" />
  <img v-else-if="mode === 'native'" v-bind="$attrs" :src="authUrl" ref="authImhRef" />
  <a-image v-else v-bind="$attrs" :src="authUrl" />
</template>

<script setup>
import { ref, reactive, watch, defineProps, onUnmounted, defineExpose } from 'vue'
import { MoreOutlined } from '@ant-design/icons-vue'
import { message as Message } from 'ant-design-vue'
import { getToken, TokenKey } from '@/utils/auth'
import { getCachedVoiceText, setCachedVoiceText } from '@/utils/voiceTextCache'
import { voiceToText } from '@/api/chat'
import axios from 'axios'
import defaultImg from '@/assets/img/defaultImg.png'
const authUrl = ref('')
const authBlob = ref(null)
const authImhRef = ref()

const props = defineProps({
  src: {
    type: String,
    default: ''
  },
  mode: {
    // 原生组件和antd组件的图片模式不同，需要单独传参，默认native
    type: String,
    default: 'native'
  },
  menu: {
    // 音频模式是否启用自定义菜单（下载/播放速度/语音转文字）
    type: Boolean,
    default: false
  }
})
defineExpose({
  authImhRef
})

watch(
  () => props.src,
  newVal => {
    downloadFileApi(newVal)
  },
  {
    immediate: true
  }
)

// ===== 音频自定义菜单（下载/播放速度/语音转文字）=====
// 音频容器节点，用于获取内部的 audio 元素
const rootRef = ref(null)
const menuVisible = ref(false)
const speedVisible = ref(false)
const playbackRate = ref(1)
const playbackRateOptions = [0.5, 0.75, 1, 1.25, 1.5, 2]
// 语音转文字状态
const voiceText = reactive({ text: '', loading: false, visible: false })

// src 变化时重置菜单相关状态，并回填本地缓存的转写文本；命中缓存的语音进入页面直接展示，无需手动点击
watch(
  () => props.src,
  () => {
    voiceText.text = getCachedVoiceText(props.src)
    voiceText.visible = !!voiceText.text
    voiceText.loading = false
    playbackRate.value = 1
  },
  { immediate: true }
)

function setRootRef(el) {
  rootRef.value = el
}
// 获取当前 audio 元素
function getAudioEl() {
  return rootRef.value?.querySelector('audio')
}
// 菜单展开/收起，收起时重置倍速子菜单
function handleMenuVisibleChange(visible) {
  menuVisible.value = visible
  if (!visible) speedVisible.value = false
}
// 设置播放倍速
function handleSetPlaybackRate(rate) {
  playbackRate.value = rate
  const audioEl = getAudioEl()
  if (audioEl) audioEl.playbackRate = rate
  menuVisible.value = false
  speedVisible.value = false
}
// 同步倍速：src 变化触发 load 或元素重建时浏览器会把 playbackRate 重置回 1，
// 在 play（点击播放/播完重播）和 canplay（加载完成）时用已保存的倍速兜底恢复
function syncPlaybackRate() {
  const audioEl = getAudioEl()
  if (audioEl && audioEl.playbackRate !== playbackRate.value) {
    audioEl.playbackRate = playbackRate.value
  }
}
// 语音转文字：优先用本地缓存的转写文本，无缓存时下载音频上传识别，成功后写入缓存
async function handleVoiceToText() {
  menuVisible.value = false
  if (voiceText.text) {
    voiceText.visible = !voiceText.visible
    return
  }
  if (voiceText.loading) return
  voiceText.loading = true
  voiceText.visible = true
  try {
    let blob = authBlob.value
    // 音频未经鉴权下载（非文件域名地址）时兜底下载一次并缓存
    if (!blob) {
      const res = await axios.get(props.src, {
        headers: {
          [TokenKey]: getToken()
        },
        responseType: 'blob'
      })
      blob = authBlob.value = res.data
    }
    // 从地址中取文件扩展名，兜底 wav
    const ext = (props.src.split('?')[0].match(/\.(\w+)$/) || [])[1] || 'wav'
    const formData = new FormData()
    formData.append('file', new File([blob], `voice.${ext}`, { type: blob.type }))
    const res = await voiceToText(formData)
    if (res) {
      // 识别成功写入本地缓存，下次进入会话直接展示
      setCachedVoiceText(props.src, res)
    }
    voiceText.text = res || ''
    if (!res) Message.warning('未识别到语音内容')
  } catch (e) {
    voiceText.visible = false
  } finally {
    voiceText.loading = false
  }
}
// 下载音频（原生菜单项已被 controlsList 移除，由自定义菜单提供）
function handleDownload() {
  menuVisible.value = false
  const url = getAudioEl()?.src
  if (!url) return
  // blob 地址（已鉴权下载的本地副本）直接触发下载；远程地址新开标签页兜底
  if (!url.startsWith('blob:')) {
    window.open(url, '_blank')
    return
  }
  const ext = props.src.split('?')[0].match(/\.(\w+)$/)
  const link = document.createElement('a')
  link.href = url
  link.download = `语音_${Date.now()}.${ext ? ext[1] : 'mp3'}`
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

// 下载文件
async function downloadFileApi(url) {
  authUrl.value = defaultImg
  authBlob.value = null
  if (url && (url.includes('file.drstrong') || url.includes('filetest4.drstrong'))) {
    authUrl.value = await getImgsource(url)
  } else if (url.includes('@/assets/')) {
    // 兼容本地图片
    authUrl.value = require(`@/assets/${url.replace('@/assets/', '')}`)
  } else {
    authUrl.value = url
  }
}
// 请求服务器获取资源
async function getImgsource(url) {
  return new Promise(resolve => {
    axios
      .get(url, {
        headers: {
          [TokenKey]: getToken()
        },
        responseType: 'blob'
      })
      .then(res => {
        if (res.status === 200 && res.data) {
          authBlob.value = res.data
          const fileURL = URL.createObjectURL(res.data)
          resolve(fileURL)
        } else {
          resolve('')
        }
      })
      .catch(err => {
        resolve('')
      })
  })
}

onUnmounted(() => {
  if (authUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(authUrl.value)
  }
})
</script>
<style scoped lang="scss">
// 音频 + 自定义菜单（原生下载/倍速菜单项已通过 controlsList 移除）
.audio-with-menu {
  display: flex;
  align-items: center;
  gap: 2px;

  audio {
    flex: 1 1 auto;
    min-width: 0;
  }

  .audio-menu-trigger {
    flex-shrink: 0;
    padding: 0 4px;
    color: #333333;
  }

  .speed-arrow {
    font-size: 10px;
    color: #999999;
  }
}

.menu-item {
  width: 84px;
  cursor: pointer;
  color: #666666;

  &:not(:last-child) {
    border-bottom: 1px solid #e8e8e8;
    padding-bottom: 8px;
    margin-bottom: 8px;
  }

  &.speed-option {
    padding-left: 16px;

    &.active {
      color: #1890ff;
    }
  }
}

// 语音转文字展示区
.voice-text-content {
  width: fit-content;
  max-width: 297px;
  margin-top: 4px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f2f2f2;
  color: #1b1b1b;
  font-size: 15px;
  line-height: 22px;
  white-space: pre-wrap;
  box-sizing: border-box;
}

// 识别中状态
.voice-text-loading {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #999999;
}

// 面板淡入过渡
.voice-text-fade-enter-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.voice-text-fade-enter-from {
  opacity: 0;
  transform: translateY(4px);
}
</style>
